/**
 * The custom color field — the interactive hue × lightness square shared by
 * the desktop CUSTOM COLOR modal and the phone sheet (`ccCanvas` / `ccDown` /
 * `ccMove` / `ccUp` in both shells; DESIGN-CHANGELOG 2026-08-18).
 *
 * The paint is `lib/color.ts#fieldHL` pixel by pixel — the same function the
 * committed value is read from, which is the picker's whole contract. Two
 * rendering rules from the design, both load-bearing:
 *
 *  - The finished paint gets ONE centred 1.4px blur at native size: sRGB has
 *    two corners (blue ~h265, green ~h144) where the most saturated color a
 *    hue can hold changes abruptly, and the one-pixel crease through them
 *    reads as a drawn line. Centred and unscaled deliberately — an offset or
 *    magnified composite moves the paint away from the value under the
 *    crosshair.
 *  - The crisp bitmap stays UNDERNEATH the blurred draw: the blur samples
 *    past the bitmap edge, so blurring over a cleared canvas left a
 *    translucent fringe where the panel showed through and the paint
 *    disagreed with the value at the very edge. Alpha is 255 to the corners.
 *
 * The pixel buffer is computed once per session and kept at module level —
 * the boundary search behind it is ~500k gamut tests, a visible beat on a
 * phone if every open repeats it.
 *
 * Pointer contract: press-and-drag with pointer capture so the pick keeps
 * tracking when the pointer leaves the square; the move handler bails on
 * `buttons === 0` so a missed pointerup can never leave a drag armed. Marker
 * children stay hoverable (they are the only thing that names a member, and
 * initials are ambiguous — M is MARCUS and MIKE); the drag still works
 * through them because the events bubble to the square.
 */

import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { useRef } from 'react';

import { fieldHL, okRgb } from '../lib/color.ts';

const W = 224;

let pixels: Uint8ClampedArray | null = null;

function buildPixels(): Uint8ClampedArray {
  if (pixels) return pixels;
  const buf = new Uint8ClampedArray(W * W * 4);
  for (let j = 0; j < W; j++) {
    for (let i = 0; i < W; i++) {
      const c = fieldHL(i / (W - 1), j / (W - 1));
      const rgb = okRgb(c.L, c.C, c.h);
      const o = (j * W + i) * 4;
      buf[o] = rgb[0];
      buf[o + 1] = rgb[1];
      buf[o + 2] = rgb[2];
      buf[o + 3] = 255;
    }
  }
  pixels = buf;
  return buf;
}

export interface ColorFieldProps {
  /** a press or drag landed at (u, v) ∈ [0,1]² */
  onPoint: (u: number, v: number) => void;
  /** markers + crosshair, absolutely positioned over the paint */
  children?: ReactNode;
}

export function ColorField({ onPoint, children }: ColorFieldProps): JSX.Element {
  const dragging = useRef(false);

  const setFrom = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const r = e.currentTarget.getBoundingClientRect();
    onPoint((e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height);
  };

  return (
    <div
      onPointerDown={(e) => {
        dragging.current = true;
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* capture is best-effort — a synthetic event has no pointerId */
        }
        setFrom(e);
      }}
      onPointerMove={(e) => {
        if (e.buttons === 0) {
          dragging.current = false;
          return;
        }
        if (dragging.current) setFrom(e);
      }}
      onPointerUp={(e) => {
        dragging.current = false;
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          /* released already, or never captured */
        }
      }}
      onPointerCancel={() => {
        dragging.current = false;
      }}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '1',
        border: '1px solid var(--gt-ink)',
        cursor: 'crosshair',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      <canvas
        ref={(cv) => {
          if (!cv || cv.dataset['painted']) return;
          cv.width = W;
          cv.height = W;
          const ctx = cv.getContext('2d');
          if (!ctx) return;
          const img = ctx.createImageData(W, W);
          img.data.set(buildPixels());
          ctx.putImageData(img, 0, 0);
          try {
            const off = document.createElement('canvas');
            off.width = W;
            off.height = W;
            const octx = off.getContext('2d');
            if (octx) {
              octx.putImageData(img, 0, 0);
              ctx.filter = 'blur(1.4px)';
              ctx.drawImage(off, 0, 0, W, W);
              ctx.filter = 'none';
            }
          } catch {
            /* a runtime without canvas filters keeps the crisp paint */
          }
          cv.dataset['painted'] = '1';
        }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', pointerEvents: 'none' }}
      />
      {children}
    </div>
  );
}
